import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { readStoredAuthSession, type StoredAuthSession } from '@delta/auth';
import type { CurrentUser, LoginResponse } from '@delta/domain';
import { api, authStorage } from '../lib/api';
import { clearSecureQueryCache, TENANT_SECURE_QUERY_ROOT } from '../shared/querySecurity';

interface AuthContextValue {
  token: string | null;
  user: CurrentUser | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<StoredAuthSession<CurrentUser> | null>(
    () => readStoredAuthSession(authStorage),
  );
  const token = session?.token ?? null;
  const user = session?.user ?? null;

  const login = useCallback(async (username: string, password: string) => {
    const { data } = await api.post<LoginResponse>('/auth/login', { username, password });
    clearSecureQueryCache(queryClient, TENANT_SECURE_QUERY_ROOT);
    authStorage.setToken(data.accessToken);
    authStorage.setUser(data.user);
    setSession({ token: data.accessToken, user: data.user });
  }, [queryClient]);

  const logout = useCallback(() => {
    clearSecureQueryCache(queryClient, TENANT_SECURE_QUERY_ROOT);
    authStorage.clear();
    setSession(null);
  }, [queryClient]);

  useEffect(() => {
    const renewed = () => setSession(readStoredAuthSession(authStorage));
    window.addEventListener('delta:unauthorized', logout);
    window.addEventListener('delta:token-renewed', renewed);
    return () => {
      window.removeEventListener('delta:unauthorized', logout);
      window.removeEventListener('delta:token-renewed', renewed);
    };
  }, [logout]);

  const value = useMemo(() => ({ token, user, login, logout }), [login, logout, token, user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必须在 AuthProvider 内使用');
  }
  return context;
}
